import { hex, shade, W, type Col } from "../art/pal.ts";
import { drawText, measureText } from "../engine/font.ts";
import { hash2 } from "./noise.ts";
import type { PropSprite } from "./props.ts";
import { Spr } from "./spr.ts";

export type BuildingKind = "clinic" | "stall" | "box" | "house" | "market" | "gate" | "crownhouse" | "hall";

const INK = hex("#1a1430");
const cache = new Map<string, PropSprite>();

/** Top overhang above the footprint (chimney, roof ridge). */
export const ROOF_PAD = 14;
/** Side eaves beyond the footprint. */
export const EAVE = 3;

interface Style {
  roof: readonly Col[];
  wall: readonly Col[];
  label: string;
  awning?: [Col, Col];
}

const STYLES: Record<BuildingKind, Style> = {
  clinic: { roof: W.roofRed, wall: W.wall, label: "CLINIC" },
  stall: { roof: W.roofTeal, wall: [hex("#fff3dc"), hex("#f4dfb8"), hex("#e0c395"), hex("#b99a74"), hex("#8a6e58")], label: "STALL", awning: [hex("#40c4b0"), hex("#fff6e6")] },
  box: { roof: W.roofBlue, wall: [hex("#e6ecff"), hex("#c9d2f0"), hex("#a3add6"), hex("#7c85b0"), hex("#565d85")], label: "BOX" },
  house: { roof: W.roofBlue, wall: W.wall, label: "" },
  market: { roof: [hex("#ffc4f2"), hex("#ff7ae6"), hex("#e03ccf"), hex("#a0249c"), hex("#5e1464")],
    wall: [hex("#f0f2ff"), hex("#d8dcf4"), hex("#b8bee0"), hex("#8e96bc"), hex("#626a90")], label: "MARKET", awning: [hex("#ff5ad8"), hex("#fff6fc")] },
  gate: { roof: [hex("#b8a8ff"), hex("#7c68e0"), hex("#5240b4"), hex("#342886"), hex("#1e1656")],
    wall: [hex("#8a86a8"), hex("#6e6a8e"), hex("#585476"), hex("#44405e"), hex("#302c46")], label: "RAID" },
  crownhouse: { roof: [hex("#fff2a4"), hex("#ffc83e"), hex("#d98a26"), hex("#8e5022"), hex("#5a3016")],
    wall: [hex("#fff7e2"), hex("#f4e2bc"), hex("#dcc394"), hex("#b99a74"), hex("#8a6e58")], label: "CROWNS", awning: [hex("#ffc83e"), hex("#fff7e2")] },
  hall: { roof: [hex("#e0dcf0"), hex("#b8b2d0"), hex("#8e88ac"), hex("#625c80"), hex("#3e3856")],
    wall: [hex("#f4f0ea"), hex("#dcd6cc"), hex("#bcb4a8"), hex("#968e84"), hex("#706a64")], label: "HALL" },
};

/**
 * A 3/4-view building filling a footprint of `tw`×`th` tiles, doors at footprint columns `doors`.
 * The sprite is anchored with (ax, ay) at the footprint's bottom-centre ground pixel.
 */
export function buildingSprite(kind: BuildingKind, tw: number, th: number, doors: readonly number[], night: boolean): PropSprite {
  const key = `${kind}:${tw}:${th}:${doors.join(",")}:${night}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const st = STYLES[kind];
  const FW = tw * 16, FH = th * 16;
  const w = FW + EAVE * 2, h = FH + ROOF_PAD;
  const s = new Spr(w, h);
  const x0 = EAVE, y0 = ROOF_PAD; // footprint origin in sprite space
  const facadeH = th >= 4 ? 30 : th >= 3 ? 24 : 18;
  const fy = y0 + FH - facadeH; // facade top
  const roofTop = y0 - 6;
  const roofBot = fy + 3;

  // --- Facade ---------------------------------------------------------------------------------
  const wall = st.wall;
  for (let y = fy; y < y0 + FH; y++) {
    for (let x = x0; x < x0 + FW; x++) {
      const t = (x - x0) / FW;
      let c = wall[1]!;
      if (t > 0.9) c = wall[2]!;
      if ((x * 7 + y * 13) % 23 === 0) c = wall[2]!;
      if (hash2(x, y, 11) < 0.04) c = wall[0]!;
      s.p(x, y, c);
    }
  }
  // Timber frame: corner posts and a sill beam.
  const wd = W.wood;
  for (const px of [x0, x0 + FW - 3]) {
    s.fill(px, fy, 3, facadeH, wd[2]!);
    s.vline(px, fy, facadeH, wd[1]!);
  }
  s.fill(x0, y0 + FH - 3, FW, 3, wd[3]!);
  s.hline(x0, y0 + FH - 3, FW, wd[2]!);
  s.fill(x0, y0 + FH - 1, FW, 1, wd[4]!);

  // Doors.
  const doorW = 12, doorH = Math.min(18, facadeH - 5);
  const doorXs = doors.map((col) => x0 + col * 16 + 2);
  for (const dx of kind === "gate" ? [] : doorXs) {
    s.fill(dx - 1, y0 + FH - doorH - 4, doorW + 2, doorH + 1, wd[3]!);
    s.fill(dx, y0 + FH - doorH - 3, doorW, doorH, wd[1]!);
    for (let x = dx + 2; x < dx + doorW; x += 3) s.vline(x, y0 + FH - doorH - 3, doorH, wd[2]!);
    s.hline(dx, y0 + FH - doorH - 3, doorW, wd[0]!);
    if (night) {
      s.fill(dx + 2, y0 + FH - doorH - 1, doorW - 4, 4, hex("#ffd27a"));
      s.hline(dx + 2, y0 + FH - doorH - 1, doorW - 4, hex("#fff2c0"));
    } else {
      s.fill(dx + 3, y0 + FH - doorH - 1, doorW - 6, 4, hex("#5d86c8"));
      s.p(dx + 3, y0 + FH - doorH - 1, hex("#cfe6ff"));
    }
    s.p(dx + doorW - 3, y0 + FH - 11, hex("#ffd84a"));
    // Step.
    s.fill(dx - 2, y0 + FH - 3, doorW + 4, 2, W.rock[1]!);
    s.hline(dx - 2, y0 + FH - 3, doorW + 4, W.rock[0]!);
  }

  // Raid gate: a swirling portal arch instead of a wooden door.
  if (kind === "gate") {
    for (const dx of doorXs) {
      const cx = dx + doorW / 2, cy = y0 + FH - 10, rx = 8, ry = 11;
      for (let y = cy - ry - 2; y <= y0 + FH - 1; y++) {
        for (let x = Math.floor(cx - rx - 2); x <= Math.ceil(cx + rx + 2); x++) {
          const nx = (x + 0.5 - cx) / rx, ny = Math.min(0, (y + 0.5 - cy)) / ry;
          const d = Math.sqrt(nx * nx + ny * ny);
          if (d > 1.2) continue;
          if (d > 1) { s.p(x, y, W.rock[d > 1.1 ? 2 : 0]!); continue; }
          const a = Math.atan2(y - cy, x - cx);
          const swirl = Math.sin(a * 3 + d * 9);
          s.p(x, y, swirl > 0.4 ? hex("#b48cff") : swirl > -0.2 ? hex("#6a4ad8") : d < 0.35 ? hex("#e8fbff") : hex("#3ef0e0"));
        }
      }
      s.fill(dx - 4, y0 + FH - 3, doorW + 8, 2, W.rock[1]!);
      s.hline(dx - 4, y0 + FH - 3, doorW + 8, W.rock[0]!);
    }
  }

  // Windows between doors and corners.
  const winY = fy + Math.max(5, Math.floor((facadeH - 14) / 2));
  const winH = Math.min(10, facadeH - 12);
  const blocked = (x: number) => doorXs.some((dx) => x + 12 > dx - 4 && x < dx + doorW + 4);
  for (let x = x0 + 6; x + 12 <= x0 + FW - 5; x += 18) {
    if (blocked(x) || winH < 5) continue;
    s.fill(x - 1, winY - 1, 12, winH + 2, wd[3]!);
    for (let yy = 0; yy < winH; yy++) {
      for (let xx = 0; xx < 10; xx++) {
        const c = night
          ? yy < 2 ? hex("#fff2c0") : hex("#ffc867")
          : (xx - yy + 12) % 9 < 2 ? hex("#e8f6ff") : yy < winH / 2 ? hex("#8fc6ff") : hex("#5c8fd6");
        s.p(x + xx, winY + yy, c);
      }
    }
    s.vline(x + 5, winY, winH, wd[2]!);
    s.hline(x, winY + (winH >> 1), 10, wd[2]!);
    // Flower box.
    s.fill(x - 1, winY + winH + 1, 12, 2, wd[2]!);
    for (let k = 0; k < 5; k++) s.p(x + k * 2, winY + winH, W.flowers[(k + x) % 4]!);
  }

  // Awning (stall).
  if (st.awning) {
    const ay = fy + 1;
    for (let x = x0 - 1; x < x0 + FW + 1; x++) {
      const stripe = Math.floor((x - x0 + 1) / 5) % 2 === 0 ? st.awning[0] : st.awning[1];
      for (let y = ay; y < ay + 7; y++) s.p(x, y, y === ay ? shade(stripe, 1.08) : stripe);
      const scallop = ((x - x0 + 1) % 5 === 2 || (x - x0 + 1) % 5 === 3) ? 1 : 0;
      s.p(x, ay + 7 + scallop, stripe);
      s.p(x, ay + 7, stripe);
    }
    for (let x = x0 - 1; x < x0 + FW + 1; x++) s.p(x, ay + 9, hex("#1a1430", 60));
  }

  // --- Roof -----------------------------------------------------------------------------------
  const roof = st.roof;
  const inset = Math.min(10, Math.floor(FW * 0.12));
  for (let y = roofTop; y <= roofBot; y++) {
    const t = (y - roofTop) / (roofBot - roofTop);
    const l = Math.round(x0 + inset * (1 - t)) - (y > roofBot - 3 ? EAVE : Math.round(EAVE * t));
    const r = Math.round(x0 + FW - inset * (1 - t)) + (y > roofBot - 3 ? EAVE : Math.round(EAVE * t));
    for (let x = l; x < r; x++) {
      const row = Math.floor((y - roofTop) / 4);
      const seam = (x + (row % 2) * 3) % 6 === 0;
      const rowLine = (y - roofTop) % 4 === 3;
      const u = (x - l) / Math.max(1, r - l);
      let lam = 0.35 - u * 0.5 + (1 - t) * 0.25;
      if (rowLine) lam -= 0.55;
      if (seam && !rowLine) lam -= 0.25;
      if (x === l || x === l + 1) lam += 0.5;
      if (x >= r - 2) lam -= 0.5;
      s.p(x, y, Spr.tone(roof, Math.max(-1, Math.min(1, lam)), x, y, 0.2));
    }
  }
  // Ridge cap.
  for (let x = x0 + inset - 1; x < x0 + FW - inset + 1; x++) {
    s.p(x, roofTop, roof[0]!);
    s.p(x, roofTop + 1, roof[1]!);
  }
  // Eave edge + drip shadow onto the facade.
  for (let x = x0 - EAVE; x < x0 + FW + EAVE; x++) {
    s.p(x, roofBot, roof[3]!);
    s.p(x, roofBot + 1, roof[4]!);
    for (let k = 2; k < 5; k++) {
      if (s.alpha(x, roofBot + k)) {
        const c = s.get(x, roofBot + k);
        s.p(x, roofBot + k, shade(c, 0.62 + k * 0.07));
      }
    }
  }
  // Chimney.
  if (kind === "clinic" || kind === "house") {
    const cx = x0 + FW - inset - 14;
    s.fill(cx, roofTop - 7, 7, 11, hex("#b85a4a"));
    s.fill(cx, roofTop - 7, 2, 11, hex("#e0826a"));
    s.fill(cx - 1, roofTop - 8, 9, 2, hex("#6b3a3a"));
    for (let y = roofTop - 5; y < roofTop + 4; y += 3) s.hline(cx + 2, y, 5, hex("#8f4040"));
  }

  // Sign board over the door.
  if (st.label && doorXs.length) {
    const lw = measureText(st.label) + 8;
    const cx = doorXs[0]! + (doorW >> 1);
    const sx = Math.round(cx - lw / 2);
    const sy = fy - 5;
    s.fill(sx, sy, lw, 11, hex("#2b2238"));
    s.fill(sx + 1, sy + 1, lw - 2, 9, kind === "clinic" ? hex("#ff6f91") : kind === "stall" ? hex("#ffc84a")
      : kind === "market" ? hex("#ff8ae6") : kind === "gate" ? hex("#b48cff") : hex("#58e0cf"));
    s.hline(sx + 1, sy + 1, lw - 2, hex("#ffffff", 150));
    drawText(s.img, sx + 4, sy + 2, st.label, [43, 34, 56, 255]);
  }

  s.outline(INK, 0.35);
  const sprite = { img: s.img, ax: x0 + (FW >> 1), ay: y0 + FH - 1 };
  cache.set(key, sprite);
  return sprite;
}
